import { useMemo } from 'react';
import { object } from 'underscore';
import moment from 'moment';
import { SubmittingStatus } from 'redux/schemas/models/exercise';

export type ApprovalStatusFlavor = {
  bg: string;
  translateKey: string;
};

export enum ApprovalStatus {
  APPROVED = 'approved',
  PENDING_APPROVAL = 'pending_approval',
  REJECTED_CAN_REVISE = 'rejected_can_revise',
  REJECTED_CANNOT_REVISE = 'rejected_cannot_revise',
}

const approvalStatusFlavors = object([
  [ApprovalStatus.APPROVED, 'bg-color-success', 'APPROVED'],
  [ApprovalStatus.PENDING_APPROVAL, 'bg-color-primary', 'PENDING'],
  [ApprovalStatus.REJECTED_CAN_REVISE, 'bg-color-warning', 'REJECTED'],
  [ApprovalStatus.REJECTED_CANNOT_REVISE, 'bg-color-gray', 'REJECTED_PAST'],
].map<[string, ApprovalStatusFlavor]>((f) => ([
  f[0], {
    bg: f[1],
    translateKey: f[2],
  },
])));

export const getApprovalStatus = (submittingStatus: SubmittingStatus, exerciseIsMissing: boolean): ApprovalStatus | null => {
  if (submittingStatus === SubmittingStatus.APPROVED) {
    return ApprovalStatus.APPROVED;
  }

  if (submittingStatus === SubmittingStatus.PENDING_APPROVAL) {
    return ApprovalStatus.PENDING_APPROVAL;
  }

  if (submittingStatus === SubmittingStatus.REJECTED) {
    return exerciseIsMissing
      ? ApprovalStatus.REJECTED_CANNOT_REVISE
      : ApprovalStatus.REJECTED_CAN_REVISE;
  }

  return null;
};

export const useApprovalStatus = (submissionStatus, hardDeadline, deadline) => useMemo(() => {
  const exerciseIsMissing = Boolean(hardDeadline) && moment(deadline) < (moment());

  const approvalStatus = getApprovalStatus(submissionStatus, exerciseIsMissing);

  const isCompleted = submissionStatus === SubmittingStatus.COMPLETED;
  const isPendingApproval = approvalStatus === ApprovalStatus.PENDING_APPROVAL;
  const isApproved = approvalStatus === ApprovalStatus.APPROVED;
  const isRejectedCanRevise = approvalStatus === ApprovalStatus.REJECTED_CAN_REVISE;

  const approvalStatusFlavor = approvalStatusFlavors[approvalStatus];

  return {
    isApproved,
    isCompleted,
    isPendingApproval,
    isRejectedCanRevise,
    approvalStatusFlavor,
  };
}, [deadline, hardDeadline, submissionStatus]);


export const MINUTE_THRESHOLD = 60 * 1000;
export const HOUR_THRESHOLD = 60 * MINUTE_THRESHOLD;
export const DAY_THRESHOLD = 24 * HOUR_THRESHOLD;

export const calculateTimeDifference = (deadlineDate: moment.Moment) => {
  const timeDifference = deadlineDate.valueOf() - moment().valueOf();
  return {
    days: Math.floor(timeDifference / DAY_THRESHOLD),
    hours: Math.floor(timeDifference / HOUR_THRESHOLD),
    minutes: Math.floor(timeDifference / MINUTE_THRESHOLD),
  };
};
